import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { trpc } from 'shared/api'
import { PageFrame } from 'shared/components/page-frame'
import { PointSelector } from 'shared/components/point-selector'
import { CompactTable, SectionTitle } from 'shared/components/section'
import { Button } from 'shared/components/ui/button'
import { Label } from 'shared/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/components/ui/select'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useTranslate } from 'shared/lib/i18n'
import { actsAsAdmin, type Role } from 'shared/lib/roles'
import { formatDayTime } from 'shared/lib/utils/date'
import { useAuthStore } from 'shared/store/auth'
import { useLocationScope } from 'shared/store/location-scope'
import { NetworkRemainders } from './NetworkRemainders'
import { PointRemainders } from './PointRemainders'
import { PointStateBanner } from './PointStateBanner'
import { RemaindersDownload } from './RemaindersDownload'
import { LabelWithHint } from './remainders-parts'

/**
 * «Остатки» — the remainders of a Point and of the network (Shohrux's Ф4; ticket
 * 47), the first screen of the admin, the operator and support (`/dashboard`).
 *
 * A **reader and nothing else**: every number is the server's (`remainders.AtPoint`,
 * `remainders.Network`), counted from the current stages, holders and Money Events
 * — nothing here counts — and «Скачать Excel» in the header asks the server for a
 * file of exactly the same read.
 *
 * The operator sees their own Point (the place on their account, no picker). The
 * admin and support land on «Сеть» and switch to «Пункт»: the admin picks the Point
 * with the shared picker, support from the network's Points; a row of the network
 * opens its Point. Support's answers carry no money (ADR-0029), so no cash shows.
 * Below, the queues that wait for a person — only those that hold something and
 * that this person may open.
 *
 * `opens` says whether the person sees a section — so a link leads only where they
 * may go; the route map passes it in.
 */
export function Dashboard({ opens = () => true }: { opens?: (role: Role, path: string) => boolean }) {
	const { t } = useTranslate()
	const role = useAuthStore((s) => s.user.role)
	const isAdmin = actsAsAdmin(role)
	const seesNetwork = actsAsAdmin(role) || role === 'support'
	const scope = useLocationScope()
	const [view, setView] = useState<'point' | 'network'>(seesNetwork ? 'network' : 'point')
	// Support picks a Point here, for this screen only; the admin's choice is shared with other screens.
	const [supportPoint, setSupportPoint] = useState<string | null>(null)
	const pointId = scope.isBound ? scope.locationId : isAdmin ? scope.locationId : supportPoint
	// An operator sends no `pointId`: the server takes their own.
	const asked = scope.isBound ? undefined : (pointId ?? undefined)
	const network = seesNetwork && view === 'network'

	const openPoint = (id: string) => {
		if (isAdmin) scope.setLocationId(id)
		else setSupportPoint(id)
		setView('point')
	}

	return (
		<PageFrame
			title='remainders.title'
			actions={
				<RemaindersDownload
					input={network ? { scope: 'network' } : { scope: 'point', pointId: asked }}
					disabled={!network && !pointId}
				/>
			}
		>
			<div className='flex flex-wrap items-center gap-x-6 gap-y-3'>
				{seesNetwork ? <ViewSwitch view={view} onChange={setView} /> : null}
				{network ? null : role === 'support' ? (
					<SupportPointPicker value={supportPoint} onChange={setSupportPoint} />
				) : (
					<PointSelector
						id='dashboard-point'
						testId='dashboard-point'
						label={t('remainders.selectPoint')}
						placeholder={t('remainders.pointPlaceholder')}
						// Beside «Пункт / Сеть» the word «Пункт» is said already: the label is for screen readers.
						className={seesNetwork ? 'flex items-center gap-2 [&>label]:sr-only' : 'flex items-center gap-2'}
					/>
				)}
				{network ? <NetworkAsOf /> : pointId ? <PointAsOf pointId={asked} /> : null}
			</div>

			{network ? (
				<NetworkSection onOpenPoint={openPoint} />
			) : pointId ? (
				<PointSection
					pointId={asked}
					opens={(path) => opens(role, path)}
					showState={role === 'operator' || actsAsAdmin(role)}
				/>
			) : scope.isBound ? null : (
				// A bound account with no place is told so by the Point field above; only a picker asks to choose.
				<p className='text-sm text-muted-foreground' data-testid='dashboard-choose-point'>
					{t('remainders.choosePoint')}
				</p>
			)}

			<Queues isAdmin={isAdmin} opens={(path) => opens(role, path)} />
		</PageFrame>
	)
}

/** «Пункт / Сеть» — two views of the same remainders. */
function ViewSwitch({ view, onChange }: { view: 'point' | 'network'; onChange: (view: 'point' | 'network') => void }) {
	const { t } = useTranslate()
	return (
		<div role='tablist' className='inline-flex items-center gap-0.5 rounded-lg bg-muted p-0.5'>
			{(['network', 'point'] as const).map((v) => (
				<Button
					key={v}
					role='tab'
					size='sm'
					variant={view === v ? 'outline' : 'ghost'}
					aria-selected={view === v}
					data-testid={`remainders-view-${v}`}
					onClick={() => onChange(v)}
				>
					{t(v === 'point' ? 'remainders.viewPoint' : 'remainders.viewNetwork')}
				</Button>
			))}
		</div>
	)
}

/** When the server counted what the screen shows — the same read, so no second request. */
function AsOf({ at }: { at: Date | undefined }) {
	const { t } = useTranslate()
	if (!at) return null
	return (
		<span className='ml-auto text-sm text-muted-foreground' data-testid='remainders-as-of'>
			{t('remainders.asOf', { time: formatDayTime(at) })}
		</span>
	)
}

function PointAsOf({ pointId }: { pointId?: string }) {
	const query = useQuery(trpc.remainders.AtPoint.queryOptions({ pointId }))
	return <AsOf at={query.data?.at} />
}

function NetworkAsOf() {
	const query = useQuery(trpc.remainders.Network.queryOptions())
	return <AsOf at={query.data?.at} />
}

/** A Point's remainders: an operator sends no `pointId` (the server takes their own). */
function PointSection({
	pointId,
	opens,
	showState = false,
}: {
	pointId?: string
	opens: (path: string) => boolean
	/** The Point's closing above its remainders (ticket 45) — the operator's and the admin's. */
	showState?: boolean
}) {
	const query = useQuery(trpc.remainders.AtPoint.queryOptions({ pointId }))
	return (
		<>
			{showState && <PointStateBanner pointId={pointId} />}
			{query.data ? <PointRemainders data={query.data} opens={opens} /> : <Loading error={query.error} />}
		</>
	)
}

function NetworkSection({ onOpenPoint }: { onOpenPoint: (pointId: string) => void }) {
	const query = useQuery(trpc.remainders.Network.queryOptions())
	return query.data ? (
		<NetworkRemainders data={query.data} onOpenPoint={(p) => onOpenPoint(p.pointId)} />
	) : (
		<Loading error={query.error} />
	)
}

function Loading({ error }: { error: { message: string } | null }) {
	const { t } = useTranslate()
	return (
		<p
			className={error ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}
			role={error ? 'alert' : undefined}
			data-testid='remainders-loading'
		>
			{error ? t('remainders.failed', { message: error.message }) : t('remainders.loading')}
		</p>
	)
}

/**
 * Support's Point picker: the network's Points as its remainders list them —
 * support may not read the Locations (that is the admin's), and needs no more.
 */
function SupportPointPicker({ value, onChange }: { value: string | null; onChange: (id: string) => void }) {
	const { t } = useTranslate()
	const query = useQuery(trpc.remainders.Network.queryOptions())
	const points = query.data?.points ?? []
	return (
		<div className='flex items-center gap-2'>
			<Label htmlFor='remainders-support-point' className='sr-only'>
				{t('remainders.selectPoint')}
			</Label>
			<Select value={value ?? undefined} onValueChange={onChange}>
				<SelectTrigger id='remainders-support-point' data-testid='remainders-support-point' data-value={value ?? ''}>
					<SelectValue placeholder={t('remainders.pointPlaceholder')} />
				</SelectTrigger>
				<SelectContent position='popper' align='start'>
					{points.map((p) => (
						<SelectItem key={p.pointId} value={p.pointId}>
							{p.id}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	)
}

/**
 * The queues that wait for a person, global rather than point-scoped: a person
 * works them for the whole network, and the sections they lead to do their own
 * filtering. Only a queue that holds something and that this person may open is
 * listed; with none, the section is not there.
 */
function Queues({ isAdmin, opens }: { isAdmin: boolean; opens: (path: string) => boolean }) {
	const { t } = useTranslate()
	const navigate = useNavigate()

	const reconciliationQuery = useQuery(trpc.reconciliation.Queue.queryOptions())
	const staleQuery = useQuery(trpc.expectations.Stale.queryOptions({}))
	const deadLettersQuery = useQuery({
		...trpc.outbox.DeadLetters.queryOptions({}),
		enabled: isAdmin,
	})

	const rows = [
		{
			label: t('dashboard.reconciliation'),
			hint: t('dashboard.reconciliationHint'),
			count: reconciliationQuery.data?.length ?? 0,
			to: '/reconciliation',
			testId: 'reconciliation',
		},
		{
			label: t('dashboard.staleExpectations'),
			hint: t('dashboard.staleExpectationsHint'),
			count: staleQuery.data?.length ?? 0,
			to: '/reconciliation',
			testId: 'staleExpectations',
		},
		...(isAdmin
			? [
					{
						label: t('dashboard.deadLetters'),
						hint: t('dashboard.deadLettersHint'),
						count: deadLettersQuery.data?.length ?? 0,
						to: '/dead-letters',
						testId: 'deadLetters',
					},
				]
			: []),
	].filter((r) => r.count > 0 && opens(r.to))

	if (rows.length === 0) return null
	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle title={t('dashboard.queues')} />
			<CompactTable testId='dashboard-queues'>
				<TableHeader>
					<TableRow>
						<TableHead>{t('dashboard.colQueue')}</TableHead>
						<TableHead className='text-right'>{t('dashboard.colCount')}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((r) => (
						<TableRow
							key={r.testId}
							data-testid={`queue-${r.testId}`}
							className='cursor-pointer'
							onClick={() => navigate(r.to)}
						>
							<TableCell className='font-medium'><LabelWithHint label={r.label} hint={r.hint} /></TableCell>
							<TableCell className='text-right font-semibold text-amber-700 tabular-nums dark:text-amber-400'>
								{r.count}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</CompactTable>
		</section>
	)
}
